import { supabase } from '../config/supabase';

export interface EmpresaExistente {
  id: string;
  nome: string;
  cnpj: string;
  proprietario_id: string;
  status: string;
}

export interface ResultadoValidacao {
  existe: boolean;
  empresa?: EmpresaExistente;
  erro?: string;
}

/**
 * Valida se uma empresa já existe no sistema
 * @param cnpj - CNPJ da empresa a ser validada
 * @param usuarioId - ID do usuário atual (para verificar se é a própria empresa)
 * @returns Resultado da validação
 */
export const validarEmpresaExistente = async (
  cnpj: string, 
  usuarioId?: string
): Promise<ResultadoValidacao> => {
  try {
    if (!cnpj || cnpj.replace(/\D/g, '').length !== 14) {
      return {
        existe: false,
        erro: 'CNPJ inválido'
      };
    }

    console.log('🔍 [VALIDAÇÃO] Verificando empresa existente:', { cnpj, usuarioId });

    // Buscar empresa por CNPJ
    const { data: empresasExistentes, error } = await supabase
      .from('cp_empresas')
      .select('id, nome, cnpj, proprietario_id, status')
      .eq('cnpj', cnpj)
      .eq('status', 'ATIVA'); // Apenas empresas ativas

    if (error) {
      console.error('❌ [VALIDAÇÃO] Erro ao verificar empresa:', error);
      return {
        existe: false,
        erro: 'Erro ao verificar empresa existente'
      };
    }

    if (!empresasExistentes || empresasExistentes.length === 0) {
      console.log('✅ [VALIDAÇÃO] Empresa não existe');
      return {
        existe: false
      };
    }

    const empresaExistente = empresasExistentes[0];
    console.log('⚠️ [VALIDAÇÃO] Empresa encontrada:', empresaExistente);

    // Se o usuário está editando sua própria empresa, permitir
    if (usuarioId && empresaExistente.proprietario_id === usuarioId) {
      console.log('✅ [VALIDAÇÃO] Usuário editando sua própria empresa');
      return {
        existe: false
      };
    }

    return {
      existe: true,
      empresa: empresaExistente
    };

  } catch (error) {
    console.error('❌ [VALIDAÇÃO] Erro inesperado:', error);
    return {
      existe: false,
      erro: 'Erro inesperado ao validar empresa'
    };
  }
};

/**
 * Valida se um CNPJ está em formato válido
 * @param cnpj - CNPJ a ser validado
 * @returns true se válido, false caso contrário
 */
export const validarFormatoCNPJ = (cnpj: string): boolean => {
  const cnpjLimpo = cnpj.replace(/\D/g, '');
  return cnpjLimpo.length === 14;
};

/**
 * Gera mensagem de erro personalizada para empresa duplicada
 * @param empresa - Empresa existente
 * @returns Mensagem formatada
 */
export const gerarMensagemEmpresaDuplicada = (empresa: EmpresaExistente): string => {
  return `❌ Empresa já cadastrada!

A empresa com CNPJ ${empresa.cnpj} já está cadastrada no sistema.

📋 Detalhes da empresa existente:
• Nome: ${empresa.nome}
• CNPJ: ${empresa.cnpj}
• Status: ${empresa.status}

💡 Soluções:
• Verifique se você não está tentando cadastrar a mesma empresa
• Entre em contato com o administrador se precisar de acesso a esta empresa
• Use um CNPJ diferente se for uma empresa diferente`;
};

/**
 * Valida se o usuário pode cadastrar uma empresa
 * @param cnpj - CNPJ da empresa
 * @param usuarioId - ID do usuário
 * @param tipoUsuario - Tipo do usuário
 * @returns Resultado da validação com mensagem
 */
export const validarPermissaoCadastroEmpresa = async (
  cnpj: string,
  usuarioId: string,
  tipoUsuario: string
): Promise<{ podeCadastrar: boolean; mensagem?: string }> => {
  try {
    // Validar formato do CNPJ
    if (!validarFormatoCNPJ(cnpj)) {
      return {
        podeCadastrar: false,
        mensagem: 'CNPJ inválido. Digite um CNPJ com 14 dígitos.'
      };
    }

    // Verificar se empresa já existe
    const resultado = await validarEmpresaExistente(cnpj, usuarioId);
    
    if (resultado.erro) {
      return {
        podeCadastrar: false,
        mensagem: resultado.erro
      };
    }

    if (resultado.existe && resultado.empresa) {
      return {
        podeCadastrar: false,
        mensagem: gerarMensagemEmpresaDuplicada(resultado.empresa)
      };
    }

    return {
      podeCadastrar: true
    };

  } catch (error) {
    console.error('❌ [VALIDAÇÃO] Erro na validação de permissão:', error);
    return {
      podeCadastrar: false,
      mensagem: 'Erro ao validar empresa. Tente novamente.'
    };
  }
};
